// React
import { useNavigate } from 'react-router-dom'
// ライブラリ
import { ajax } from 'rxjs/ajax'
import { catchError, of } from 'rxjs'
import Swal from 'sweetalert2'
// Googleアカウント認証
import { initializeApp } from 'firebase/app'
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth'
// ユーティリティ
import { errorMessages } from '../../../utils/messagesUtils'

import './Google.css'

const signin = (e, navigate, homePage, setDisabled) => {
	// 通常のログインボタンとGoogleでログインボタンを無効化
	setDisabled(true)
	// ブラウザにフォーカスが戻った時に、即時にログインボタンを有効化
	/* 実装の背景
	Googleログイン認証ポップアップを閉じる動作は、catchエラーとして処理される。
	このcatchエラーの認識には体感5秒。
	その間は、通常のログインとGoogleでログインどちらも動作不可でユーザーにとってフリーズした状態となる。
	そのため、ブラウザにフォーカスが戻った時に、即時にログインボタンを有効化するようにしている。
	*/
	const onFocus = () => {
		setDisabled(false)
		window.removeEventListener('focus', onFocus)
	}
	window.addEventListener('focus', onFocus)

	const provider = new GoogleAuthProvider()
	provider.setCustomParameters({
		prompt: 'select_account',
	})
	const auth = getAuth()
	auth.languageCode = 'ja'

	signInWithPopup(auth, provider)
		.then((result) => {
			window.removeEventListener('focus', onFocus)
			const token = result.user.accessToken
			const email = result.user.email
			const uid = result.user.uid

			Swal.showLoading()

			ajax({
				url: `${process.env.REACT_APP_API_BASE_URL}/signin/google`,
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: {
					token: token,
					email: email,
					uid: uid,
				},
				withCredentials: true,
			})
				.pipe(catchError((error) => of(error)))
				.subscribe(async (res) => {
					Swal.hideLoading()
					Swal.close()

					switch (res.status) {
						case 200:
							navigate(homePage)
							break

						case 401:
							await Swal.fire({
								icon: 'error',
								html: errorMessages.google.token_not_verified,
								customClass: {
									htmlContainer: 'large-text',
								},
							})
							break

						case 404:
							await Swal.fire({
								icon: 'error',
								html: errorMessages.google.not_found,
								customClass: {
									htmlContainer: 'large-text',
								},
							})
							break

						default:
							await Swal.fire({
								icon: 'error',
								html: errorMessages.api.network_error,
								customClass: {
									htmlContainer: 'large-text',
								},
							})
							break
					}
				})
		})
	.catch(async (error) => {
		setDisabled(false)
		window.removeEventListener('focus', onFocus)
		
		// デバッグ用：エラー内容を確認
		// console.error('Google認証エラー詳細:', error)
		// console.error('エラーコード:', error.code)
		// console.error('エラーメッセージ:', error.message)
		
		// ユーザーがポップアップを閉じた場合以外はエラーを表示
		if (error.code !== 'auth/popup-closed-by-user' && 
			error.code !== 'auth/cancelled-popup-request') {
			// ユーザーフレンドリーなエラーメッセージ
			let userMessage = errorMessages.google.auth_failed
			
			switch(error.code) {
				case 'auth/popup-blocked':
					userMessage = errorMessages.google.popup_blocked
					break
				case 'auth/network-request-failed':
					userMessage = errorMessages.google.network_request_failed
					break
				case 'auth/unauthorized-domain':
					userMessage = errorMessages.google.unauthorized_domain
					break
				default:
					userMessage = errorMessages.google.auth_failed
			}
			
			await Swal.fire({
				icon: 'error',
				title: 'Google認証エラー',
				html: userMessage,
				footer: `<small>エラーコード: ${error.code}</small>`,
				customClass: {
					htmlContainer: 'large-text',
				},
			})
		}
		return
	})
}

const Google = (props) => {
	const navigate = useNavigate()

	// Firebase初期化
	initializeApp(props.fc)

	return (
		<div>
			<button
				type="button"
				className="gsi-material-button"
				disabled={props.isDisabled}
				onClick={(e) => {
					signin(e, navigate, props.homePage, props.setDisabled)
				}}
			>
				<div className="gsi-material-button-state"></div>
				<div className="gsi-material-button-content-wrapper">
					<div className="gsi-material-button-icon">
						<svg
							version="1.1"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 48 48"
							style={{ display: 'block' }}
						>
							<path
								fill="#EA4335"
								d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
							></path>
							<path
								fill="#4285F4"
								d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
							></path>
							<path
								fill="#FBBC05"
								d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
							></path>
							<path
								fill="#34A853"
								d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
							></path>
							<path fill="none" d="M0 0h48v48H0z"></path>
						</svg>
					</div>
					<span className="gsi-material-button-contents">
						Googleでログイン
					</span>
					<span style={{ display: 'none' }}>Googleでログイン</span>
				</div>
			</button>
		</div>
	)
}

export default Google
